import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useClickTracker } from "web/react/hooks/use-click-tracker/use-click-tracker";
import analytics from "web/script/analytics/analytics";
import userProfiler from "web/script/modules/userprofiler";
import globals from "web/script/modules/globals";
import { useGetSaveForLaterStatus } from "web/react/hooks/use-save-for-later/use-save-for-later";

function constructHref(href, pageViewID, reason, type, fromUrl) {
    let parsed = new URL(href, globals.window.location);

    if (pageViewID) {
        parsed.searchParams.set("pageViewId", pageViewID);
    }
    if (reason) {
        parsed.searchParams.set("reason", reason);
    }
    if (type) {
        parsed.searchParams.set("type", type);
    }
    if (fromUrl) {
        parsed.searchParams.set("fromUrl", fromUrl);
    }

    return parsed.toString();
}

/**
 * A custom hook for keeping a lead URL up to date with analytics data
 * @param {string} href The lead URL to track
 * @param {string} [reason] Optional lead reason
 * @param {string} [type] Optional lead type
 * @param {string} [fromUrl] Optional origin URL
 * @returns {string} The augmented lead URL
 */
export function useLeadHref(href, reason, type, fromUrl) {
    const [leadHref, setLeadHref] = useState(href);

    useEffect(() => {
        function updateHref() {
            let pageViewID = analytics.getPageViewId();
            setLeadHref(constructHref(href, pageViewID, reason, type, fromUrl));
        }
        updateHref();
        analytics.on("pageViewIdChanged", updateHref);
        return function cleanup() {
            analytics.off("pageViewIdChanged", updateHref);
        };
    }, [fromUrl, href, reason, type]);

    return leadHref;
}

function LeadLink({
    beforeOnClick,
    afterOnClick,
    children,
    className,
    fromUrl,
    href,
    openInNewTab,
    productId,
    reason,
    type,
}) {
    let [isSavedForLater] = useGetSaveForLaterStatus(productId);
    let leadHref = useLeadHref(href, reason, type, fromUrl);
    let trackOnClick = useClickTracker(
        "affiliate",
        "lead",
        "save_for_later_buy_on_store",
        !openInNewTab
    );

    function onClick(event) {
        if (beforeOnClick) {
            beforeOnClick(processClick, event);
        } else {
            processClick(event);
        }
    }

    // Generate href for tracked button from the props
    function processClick(event) {
        if (productId) {
            userProfiler.saveClickedLead(productId, reason);
        }

        if (isSavedForLater) {
            trackOnClick(event);
        } else if (openInNewTab) {
            // redirect-to-retailer looks for globals.window.opener which is set to null in ios
            // if we use the normal href and target so we need to trigger open in new tab
            // using window.open instead to get the correct behavior
            event.preventDefault();
            window.open(leadHref, "_blank");
        }

        afterOnClick && afterOnClick(event);
    }

    return (
        <a
            className={className}
            href={leadHref}
            onClick={onClick}
            target={openInNewTab ? "_blank" : null}
        >
            {children}
        </a>
    );
}

export default LeadLink;

LeadLink.propTypes = {
    beforeOnClick: PropTypes.func,
    afterOnClick: PropTypes.func,
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
    fromUrl: PropTypes.string,
    href: PropTypes.string.isRequired,
    openInNewTab: PropTypes.bool,
    productId: PropTypes.string,
    reason: PropTypes.string,
    type: PropTypes.string,
};

LeadLink.defaultProps = {
    openInNewTab: false,
};
